import { useCallback, useEffect, useRef, useState } from "react";
import { isSafari } from "react-device-detect";
import { useImageSize } from "react-image-size";
import { Link } from "react-router";
import PulseMobileAutoPlayPagination from "@features/pulse/components/mobile/PulseMobileAutoPlayPagination";
import PulseShareToolPopover from "@features/pulse/components/PulseShareToolPopover";
import { useGetShareCount } from "@features/pulse/hooks/useGetShareCount";
import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import {
  PulseAutoPlayPaginationProps,
  SanitizedPulseData,
} from "@features/pulse/utils/types";
import { cn, getFormattedUrlPath } from "@util/helpers";

type PulseMobileCardLayoutProps = {
  /** Indiviudal card details */
  article: SanitizedPulseData;
  /** Currently active article index */
  activeArticle?: number;
} & PulseAutoPlayPaginationProps;

/**
 * Mobile card layout for pulse
 * isSafari is used to conditionally apply styles for safari browsers as sticky bottom, rotate not fully compatible.
 * sticky bottom is used to keep the footer visible while scrolling through the content and draggable within the scrollable content for not safari browser.
 */
const PulseMobileCardLayout = ({
  article,
  autoplayTotal,
  isFront = false,
  setIsAutoplayEnabled,
  isAutoplayEnabled = true,
  resetAutoplayLeft,
  handleSetIsAutoplayPaused,
  activeArticle,
}: PulseMobileCardLayoutProps) => {
  const { sendCustomEvent } = useSendPulseGaData();
  const { shareCount, incrementShareCount } = useGetShareCount({
    articleId: article.id,
    initialShareCount: article.shareCount,
  });

  const [isPortrait, setIsPortrait] = useState<boolean>(false);
  const [dimensions] = useImageSize(article.media || "");

  const contentRef = useRef<HTMLDivElement>(null);

  const handleIncrementShareCount = () => {
    incrementShareCount();
  };

  useEffect(() => {
    if (dimensions) {
      setIsPortrait(dimensions.height > dimensions.width);
    }
  }, [dimensions]);

  const fullArticleUrl = getFormattedUrlPath(article.url, [
    { key: "ref", value: "pulse" },
  ]);

  const handleFullStoryClick = () => {
    sendCustomEvent(
      `mobile::click full story::${activeArticle}`,
      article.title
    );
  };

  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleScrollEnd = useCallback(() => {
    sendCustomEvent(`mobile::scroll::${activeArticle}>`, article.title);
  }, [sendCustomEvent, activeArticle, article.title]);

  const handleScroll = useCallback(() => {
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

    scrollTimeout.current = setTimeout(() => {
      handleScrollEnd();
    }, 200);
  }, [handleScrollEnd]);

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    contentElement.addEventListener("scroll", handleScroll);

    return () => {
      contentElement.removeEventListener("scroll", handleScroll);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [contentRef, handleScroll]);

  return (
    <>
      <div className="sticky top-0 z-10 max-h-48 min-h-48 flex-none overflow-hidden bg-white">
        {isPortrait ? (
          <div
            className="absolute z-0 h-full w-full"
            style={{
              backgroundImage: `url(${article.media})`,
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              filter: "blur(20px)",
            }}
          />
        ) : null}

        <div
          className="absolute z-0 h-full w-full"
          style={{
            backgroundImage: `url(${article.media})`,
            backgroundSize: isPortrait ? "contain" : "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          }}
        />

        <div className="relative z-10 flex h-full flex-col justify-end bg-gradient-to-t from-black/80 via-black/50 to-transparent px-4 py-3">
          {isFront ? (
            <PulseMobileAutoPlayPagination
              autoplayTotal={autoplayTotal}
              isFront={isFront}
              setIsAutoplayEnabled={setIsAutoplayEnabled}
              isAutoplayEnabled={isAutoplayEnabled}
              resetAutoplayLeft={resetAutoplayLeft}
              activeArticle={activeArticle}
              displayArticle={article}
            />
          ) : null}

          {article.sections ? (
            <p className="mb-0 pb-2 font-poppins text-4xs font-medium tracking-10 text-white">
              {article.sections?.toUpperCase()}
            </p>
          ) : null}

          <p
            className={cn(
              "line-clamp-3 font-lct text-xl font-semibold leading-130 -tracking-2 text-white"
            )}
          >
            {article.title}
          </p>
        </div>
      </div>

      {article.summaries.length > 0 ? (
        <div
          ref={contentRef}
          className={cn({
            "flex-grow overflow-y-auto scrollbar": isSafari,
          })}
        >
          <ul
            className={cn("mb-10 ml-7 list-outside list-disc pr-3 pt-4")}
            aria-label={`pulse-article-summaries-${article.id}`}
          >
            {article.summaries.map((summary, index) => (
              <li
                key={index}
                className={"mb-1 font-lucida font-normal -tracking-5"}
              >
                {summary}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div
        className={cn(
          "sticky bottom-0 mt-auto flex w-full justify-between border-t border-gray-175 bg-white text-gray-850",
          "after:from-1% after:absolute after:-top-[calc(4rem_+_1px)] after:h-16 after:w-full after:bg-gradient-to-t after:from-white after:to-90%",
          { relative: isSafari }
        )}
      >
        <PulseShareToolPopover
          articleTitle={article.title}
          shareUrl={article.shareUrl || ""}
          text="Share"
          className="w-full flex-1 rounded-none border-0 border-r border-gray-175 py-4"
          shareCount={shareCount}
          callback={handleIncrementShareCount}
          articleId={article.id}
          handleSetIsAutoplayPaused={(isPaused) => {
            handleSetIsAutoplayPaused(isPaused);
          }}
          activeArticle={activeArticle}
        />

        <Link
          to={fullArticleUrl}
          target="_blank"
          className="flex w-full flex-1 items-center justify-center py-4 text-center font-poppins text-xs font-semibold tracking-2 text-gray-850"
          onClick={handleFullStoryClick}
        >
          Full story
        </Link>
      </div>
    </>
  );
};

export default PulseMobileCardLayout;
